import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Gauge, Wallet, Percent, ShieldAlert, Target, Sparkles, Compass } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Card } from "@/components/ui/card";

const SCORE_META = [
  ["overall_score", Gauge, "advisor_score_overall"],
  ["yield_score", Percent, "advisor_score_yield"],
  ["cashflow_score", Wallet, "advisor_score_cashflow"],
  ["risk_score", ShieldAlert, "advisor_score_risk"],
  ["portfolio_fit_score", Target, "advisor_score_fit"],
];

const scoreColor = (value) => {
  if (value >= 75) return "hsl(var(--success))";
  if (value >= 50) return "hsl(var(--warning))";
  return "hsl(var(--destructive))";
};

export default function Advisor() {
  const { t, lang } = useI18n();
  const { data, isLoading } = useQuery({
    queryKey: ["advisor-overview", lang],
    queryFn: async () => (await api.get("/advisor/overview", { params: { lang } })).data,
  });

  if (isLoading || !data) return <div className="h-64 flex items-center justify-center text-muted-foreground">…</div>;

  return (
    <div className="space-y-6" data-testid="advisor-page">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-sm bg-secondary flex items-center justify-center">
          <Compass className="h-5 w-5 text-[hsl(var(--success))]" />
        </div>
        <div>
          <h1 className="font-display text-4xl">{t("advisor_title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("advisor_sub")}</p>
        </div>
      </div>

      {!data.scores ? (
        <Card className="p-16 rounded-sm border-dashed border-border text-center">
          <Gauge className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">{data.narrative}</p>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            {SCORE_META.map(([key, Icon, labelKey], i) => {
              const value = data.scores[key];
              return (
                <motion.div key={key} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                  <Card className={`p-6 rounded-sm border-border ${key === "overall_score" ? "bg-secondary/40" : ""}`}
                    data-testid={`advisor-${key}`}>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs uppercase tracking-wider text-muted-foreground">{t(labelKey)}</span>
                      <Icon className="h-4 w-4" style={{ color: scoreColor(value) }} />
                    </div>
                    <p className="font-display text-2xl tabular-nums" style={{ color: scoreColor(value) }}>{value}</p>
                  </Card>
                </motion.div>
              );
            })}
          </div>

          <Card className="p-6 rounded-sm border-border" data-testid="advisor-narrative">
            <div className="flex items-center gap-2 mb-3 text-[hsl(var(--success))]">
              <Sparkles className="h-4 w-4" />
              <h3 className="text-sm uppercase tracking-wider">{t("advisor_narrative_title")}</h3>
            </div>
            <p className="text-sm leading-relaxed">{data.narrative}</p>
            {data.priority_action && (
              <div className="mt-4 pt-4 border-t border-border">
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{t("advisor_priority_action")}</p>
                <p className="text-sm font-medium">{data.priority_action}</p>
              </div>
            )}
            {data.ai_generated === false && (
              <p className="text-xs text-muted-foreground mt-4" data-testid="advisor-ai-fallback-note">{t("ai_fallback_note")}</p>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
